import { cn } from "@productlane/lib/src/browser"

import type { HeatmapScale } from "./theme"
import { ReportingChartFrame } from "./chart-frame"
import { heatmapColorAt } from "./theme"

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

export interface ReportingHeatmapCell {
	day: number
	hour: number
	value: number
}

interface ReportingHeatmapProps {
	title: string
	description?: string
	cells: ReportingHeatmapCell[]
	isLoading?: boolean
	className?: string
	scale?: HeatmapScale
}

export const ReportingHeatmap = ({
	title,
	description,
	cells,
	isLoading,
	className,
	scale = "activity",
}: ReportingHeatmapProps) => {
	const max = Math.max(...cells.map((c) => c.value), 1)
	const lookup = new Map(cells.map((c) => [`${c.day}-${c.hour}`, c.value]))

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={cells.every((c) => c.value === 0)}
			className={className}
		>
			<div className="overflow-x-auto pb-1">
				<div className="inline-grid min-w-full grid-cols-[2.5rem_repeat(24,minmax(0.75rem,1fr))] gap-0.5">
					<div />
					{HOURS.map((h) => (
						<div key={h} className="text-center text-[10px] text-tertiary">
							{h % 3 === 0 ? h : ""}
						</div>
					))}
					{DAYS.map((day, dayIndex) => (
						<div key={day} className="contents">
							<div className="flex items-center text-xs text-tertiary">
								{day}
							</div>
							{HOURS.map((hour) => {
								const value = lookup.get(`${dayIndex}-${hour}`) ?? 0
								if (value === 0) {
									return (
										<div
											key={`${day}-${hour}`}
											title={`${day} ${hour}:00 · 0`}
											className={cn("h-4 bg-gray-100 dark:bg-gray-800")}
										/>
									)
								}
								const intensity = value / max
								return (
									<div
										key={`${day}-${hour}`}
										title={`${day} ${hour}:00 · ${value}`}
										className="h-4"
										style={{
											backgroundColor: heatmapColorAt(intensity, scale),
										}}
									/>
								)
							})}
						</div>
					))}
				</div>
				<div className="mt-3 flex items-center gap-2 pb-0.5 text-[10px] text-tertiary">
					<span>Low</span>
					<div className="flex h-2 flex-1 overflow-hidden rounded-sm">
						{[0, 0.25, 0.5, 0.75, 1].map((t) => (
							<div
								key={t}
								className="h-full flex-1"
								style={{ backgroundColor: heatmapColorAt(t, scale) }}
							/>
						))}
					</div>
					<span>High</span>
				</div>
			</div>
		</ReportingChartFrame>
	)
}
